/**
 * Stanford Hotels is modeled as a parent Zoho account plus one customer
 * row per property. Portfolio operators should see and file tickets across
 * every linked hotel, not only the empty parent account.
 */
export function isPortfolioAccount(name: string | null | undefined): boolean {
  return !!name && /stanford hotels/i.test(name);
}

function normalizePropertyName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(by|the|a|an|hotel|hotels|suites|resort|spa|and|at|property|demo)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function namesMatchProperty(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0) return true;
  const left = normalizePropertyName(a);
  const right = normalizePropertyName(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

/** Collapse duplicate Zoho customer rows that share a property name. */
export function dedupeAccountsByName<T extends {
  name: string;
  customer_id: string;
  is_primary_contact?: boolean;
  account_number?: string;
}>(accounts: T[], activeCustomerId?: string | null): T[] {
  const groups: T[][] = [];
  for (const account of accounts) {
    const group = groups.find((candidates) => namesMatchProperty(candidates[0].name, account.name));
    if (group) group.push(account);
    else groups.push([account]);
  }
  return groups.map((group) => {
    const active = group.find((account) => account.customer_id === activeCustomerId);
    if (active) return active;
    const primary = group.find((account) => account.is_primary_contact);
    if (primary) return primary;
    return [...group].sort((a, b) => (b.account_number ?? '').localeCompare(a.account_number ?? ''))[0];
  });
}
